import {applyRankersRequestHeaders, GET_HTTP_METHOD, HEAD_HTTP_METHOD} from "../helpers/request";
import {downloadWithProgress} from "../helpers/response";

export const XHR_ACCEPT_HEADERS = 'text/javascript, text/html, application/xml, text/xml, */*';
export const XHR_REQUESTED_WITH_HEADERS = 'XMLHttpRequest';

/**
 * The single front-end transport for Rankers AJAX requests, built on `fetch`.
 * Compatible with Rails' `format.js` response block and `request.xhr?`.
 *
 * It absorbs the capabilities that previously justified the separate (now removed)
 * `Loader` and `PersonFetcher` helpers — response types, download progress, and
 * urlencoded POST/PUT bodies — so there is one place that attaches the Rankers
 * identity headers (see `applyRankersRequestHeaders`). The native file-upload path
 * keeps its own dedicated XMLHttpRequest because `fetch` cannot track upload progress.
 *
 * @param {string} url - The URL to fetch.
 * @param {object} options
 * @param {string} [options.method='GET']
 * @param {string|object} [options.params] - urlencoded string or plain object.
 *        For GET/HEAD appended to the query string; otherwise sent as the
 *        `application/x-www-form-urlencoded` request body (matching the old Loader).
 * @param {'text'|'json'|'blob'} [options.responseType='text'] - how `fetch()` resolves.
 * @param {function} [options.onProgress] - called with download progress (0..1);
 *        routes the body through `downloadWithProgress` (text responses only / json after parse).
 * @param {object} [options.headers] - extra headers; these win over the defaults.
 * @param {string} [options.credentials='include']
 * @param {AbortSignal} [options.signal]
 */
export class XHRFetcher {

    constructor(url, options = {}) {
        this.url = url;
        this.responseType = options.responseType || 'text';
        this.onProgress = options.onProgress || null;
        this.request = this.buildRequest(options);
        this.content = null;
    }

    buildRequest(options = {}) {
        const method = (options.method || GET_HTTP_METHOD).toUpperCase();
        const isBodyless = method === GET_HTTP_METHOD || method === HEAD_HTTP_METHOD;

        // Include cookies in the request to approved domains (based on the cookie's `domain` value).
        // The server also needs to respond to the preflight OPTIONS request with the `Access-Control-Allow-Credentials` header set to `true`.
        // See `CrossDomainAccess#set_cross_domain_access` in the Rails app where this is set.
        const requestInit = {
            method: method,
            credentials: options.credentials ?? 'include',
        };
        if (options.signal) requestInit.signal = options.signal;

        let url = this.url;
        let bodyIsUrlencoded = false;
        const params = options.params;
        if (params !== undefined && params !== null && params !== '') {
            const serialized = (typeof params === 'string') ? params : new URLSearchParams(params).toString();
            if (isBodyless) {
                url = url + (url.indexOf('?') === -1 ? '?' : '&') + serialized;
            } else {
                requestInit.body = serialized;
                bodyIsUrlencoded = true;
            }
        } else if (options.body !== undefined && options.body !== null) {
            requestInit.body = options.body;
        }

        const request = new Request(url, requestInit);

        // Note that with CORS the server must allow these headers to be set.
        // See the Rails controller module `CrossDomainAccess#set_cross_domain_access`.
        request.headers.set('Accept', XHR_ACCEPT_HEADERS); // Important! Ensures that the Rails `format.js` response block is run.

        // Rankers identity headers (X-Requested-With + X-Rankers-User-Agent).
        // Centralised so the value can't drift; see helpers/request.js.
        applyRankersRequestHeaders(request.headers);

        // Force the urlencoded content type. NB: `new Request(url, {body: aString})` makes the
        // Fetch API auto-set `Content-Type: text/plain;charset=UTF-8`, so a presence check would
        // never fire and the server would receive an unparseable body (Rails wouldn't populate
        // params). Override unconditionally; a caller can still win via options.headers below.
        if (bodyIsUrlencoded) {
            request.headers.set('Content-Type', 'application/x-www-form-urlencoded');
        }

        // Caller-supplied headers win (e.g. an explicit `Accept`).
        if (options.headers) {
            Object.entries(options.headers).forEach(([key, value]) => request.headers.set(key, value));
        }

        return request;
    }

    // Resolves to the response content, decoded per `responseType`.
    async fetch() {
        try {
            const response = await fetch(this.request);
            return await this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async handleResponse(response) {
        if (!response.ok) throw new Error(`[XHRFetcher] Network response was not OK (${response.status})`);

        // Progress tracking reads the body stream as text; parse afterwards if JSON was requested.
        if (this.onProgress) {
            const text = await downloadWithProgress(response, this.onProgress);
            this.content = (this.responseType === 'json') ? JSON.parse(text) : text;
            return this.content;
        }

        switch (this.responseType) {
            case 'json':
                this.content = await response.json();
                break;
            case 'blob':
                this.content = await response.blob();
                break;
            default:
                this.content = await response.text();
                break;
        }
        return this.content;
    }

    handleError(error) {
        console.error('[XHRFetcher] There has been a problem with your fetch operation:', error);
        throw error;
    }

}
